import * as Sentry from '@sentry/nuxt'
import { isUnknownObject } from '~/shared/utils/guards'
import { useNotificationsStore } from '~/store/notifications'

interface DisplayErrorOptions {
  catchCallback?: () => void
  emitError?: boolean
}

export const useErrors = () => {
  const notifications = useNotificationsStore()

  function displayError<T = unknown>(
    promise: Promise<T>,
    options: DisplayErrorOptions = {}
  ): Promise<T | null> {
    return promise
      .then(data => data)
      .catch(err => {
        if (err instanceof Error) {
          notifications.pushError(err.message.trim())

          loggedError(err)

          if (typeof options.catchCallback === 'function') {
            options.catchCallback()
          }

          if (options.emitError) {
            throw err
          }
        }

        return null
      })
  }

  const ignoreError = <T = unknown>(promise: Promise<T>): Promise<T | null> => {
    return promise.then(data => data).catch(() => null)
  }

  const show404Error = (message?: string) => {
    throw createError({
      statusCode: 404,
      statusMessage: message || 'Page Not Found',
    })
  }

  const loggedError = (error: Error) => {
    if (import.meta.server) return

    const extra = isUnknownObject(error.cause) ? error.cause : undefined

    if (Sentry.isInitialized()) {
      Sentry.captureException(error, { extra })
    } else {
      console.error('[ApiError]', error)
    }
  }

  return {
    displayError,
    ignoreError,
    show404Error,
    loggedError,
  }
}
